import { useState } from "react";
import { Plus, Pencil, Trash2, FolderInput } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, img } from "../../lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { useList } from "../../pages/admin/AdminApp";
import { useCrud } from "../../lib/adminApi";
import { tr } from "../../lib/i18n";
import { fmtDuration } from "../../lib/video";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { VideoWizard, SourceIcon, PROVIDER } from "./VideoWizard";

const sel = "border rounded-md h-9 px-3 text-sm bg-white";

function BulkBar({ ids, albums, onDone }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const apply = async () => {
    if (!to) return toast.error("Hedef seçin");
    setBusy(true);
    try {
      await Promise.all(ids.map((id) => api.put(`/admin/c/videos/${id}`, { album_id: to === "none" ? null : to })));
      toast.success(`${ids.length} video ${to === "none" ? "albümden çıkarıldı" : "albüme taşındı"}`);
      qc.invalidateQueries();
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 rounded-xl bg-stone-900 text-white px-4 py-3 shadow-lg" data-testid="bulk-bar">
      <FolderInput size={16} /><span className="text-sm font-medium">{ids.length} video seçildi</span>
      <select className={`${sel} text-stone-900`} value={to} onChange={(e) => setTo(e.target.value)} data-testid="bulk-target">
        <option value="">Taşınacak yer…</option>
        {albums.map((a) => <option key={a.id} value={a.id}>{tr(a.title)}</option>)}
        <option value="none">— Albümden çıkar (tek video) —</option>
      </select>
      <Button size="sm" variant="secondary" disabled={busy} onClick={apply} data-testid="bulk-apply">Uygula</Button>
      <button className="text-xs text-stone-300 hover:text-white ml-auto" onClick={onDone} data-testid="bulk-clear">Seçimi temizle</button>
    </div>
  );
}

export default function AllVideos() {
  const { data: videos = [] } = useList("videos");
  const { data: albums = [] } = useList("video_albums");
  const { remove } = useCrud("videos");
  const [filter, setFilter] = useState("all");
  const [picked, setPicked] = useState([]);
  const [wiz, setWiz] = useState(null);
  const name = (id) => tr(albums.find((a) => a.id === id)?.title);
  const shown = videos.filter((v) => filter === "all" || (filter === "none" ? !v.album_id : v.album_id === filter));
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div className="space-y-4" data-testid="all-videos">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setWiz({})} data-testid="all-videos-add"><Plus size={15} className="mr-1" />Video ekle</Button>
        <select className={sel} value={filter} onChange={(e) => { setFilter(e.target.value); setPicked([]); }} data-testid="all-videos-filter">
          <option value="all">Tüm videolar ({videos.length})</option>
          <option value="none">Albümsüz ({videos.filter((v) => !v.album_id).length})</option>
          {albums.map((a) => <option key={a.id} value={a.id}>{tr(a.title)} ({videos.filter((v) => v.album_id === a.id).length})</option>)}
        </select>
        {shown.length > 0 && <label className="flex items-center gap-2 text-sm text-stone-600"><Checkbox checked={picked.length === shown.length} onCheckedChange={(c) => setPicked(c ? shown.map((v) => v.id) : [])} data-testid="all-videos-select-all" />Tümünü seç</label>}
      </div>
      {picked.length > 0 && <BulkBar ids={picked} albums={albums} onDone={() => setPicked([])} />}
      <div className="bg-white border rounded-xl divide-y">
        {!shown.length && <p className="p-6 text-sm text-stone-400" data-testid="all-videos-empty">Burada video yok.</p>}
        {shown.map((v) => (
          <div key={v.id} className="flex items-center gap-3 px-4 py-3" data-testid={`all-video-row-${v.slug}`}>
            <Checkbox checked={picked.includes(v.id)} onCheckedChange={() => toggle(v.id)} data-testid={`all-video-check-${v.slug}`} />
            <img src={img(v.thumbnail)} alt="" loading="lazy" decoding="async" className="w-24 aspect-video object-cover rounded bg-stone-100" />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{tr(v.title) || "(başlıksız)"}</p>
              <p className="text-xs text-stone-500 flex items-center gap-1.5 flex-wrap"><SourceIcon source={v.source} />{PROVIDER[v.source] || "YouTube"}{v.duration ? ` · ${fmtDuration(v.duration)}` : ""} · <span className={v.album_id ? "text-stone-700" : "text-amber-700"}>{v.album_id ? name(v.album_id) : "Albümsüz"}</span>{v.status === "draft" ? " · Taslak" : ""}</p>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setWiz(v)} data-testid={`all-video-edit-${v.slug}`}><Pencil size={14} /></Button>
            <Button size="icon" variant="ghost" onClick={() => remove(v.id, "Video")} data-testid={`all-video-delete-${v.slug}`}><Trash2 size={14} className="text-red-500" /></Button>
          </div>
        ))}
      </div>
      {wiz && <VideoWizard open onClose={() => setWiz(null)} albums={albums} edit={wiz.id ? wiz : null} count={videos.length} />}
    </div>
  );
}
